import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useCanRoute } from '../../lib/rbac';
import {
  LayoutDashboard, Package, MessageCircle, Mail, ShoppingCart,
  MoreHorizontal, X, Store, Warehouse as WarehouseIcon, Rocket,
  DollarSign, Settings, Bot
} from 'lucide-react';

// Primary tabs always visible in bottom nav
const PRIMARY_TABS = [
  { to: '/', icon: LayoutDashboard, label: 'nav.dashboard' },
  { to: '/orders', icon: Package, label: 'nav.orders' },
  { to: '/questions', icon: MessageCircle, label: 'nav.questions' },
  { to: '/messages', icon: Mail, label: 'nav.messages' },
] as const;

// Secondary tabs shown in the "More" sheet
const MORE_TABS = [
  { to: '/assistant', icon: Bot, label: 'nav.assistant' },
  { to: '/purchases', icon: ShoppingCart, label: 'nav.purchases' },
  { to: '/warehouse', icon: WarehouseIcon, label: 'nav.warehouse' },
  { to: '/catalog', icon: Store, label: 'nav.catalog' },
  { to: '/publisher', icon: Rocket, label: 'nav.publisher' },
  { to: '/finance', icon: DollarSign, label: 'nav.finance' },
  { to: '/settings', icon: Settings, label: 'nav.settings' },
] as const;

type NavTab = { to: string; icon: React.ElementType; label: string };

function MoreSheet({ items, onClose }: { items: readonly NavTab[]; onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-surface)] rounded-t-2xl border-t border-[var(--color-border)] shadow-lg animate-slide-up pb-[env(safe-area-inset-bottom)]">
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--color-border)]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-2">
          <span className="text-sm font-semibold text-[var(--color-text)]">{t('nav.more', 'Más')}</span>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--color-surface-hover)]">
            <X size={18} className="text-[var(--color-text-muted)]" />
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-4 gap-1 px-3 pb-4">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) => `
                  flex flex-col items-center gap-1 py-3 px-1 rounded-xl transition-colors
                  ${isActive
                    ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
                  }
                `}
              >
                <Icon size={22} />
                <span className="text-[10px] font-medium leading-tight text-center">{t(item.label)}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function BottomNavBar() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const canGo = useCanRoute();

  // Solo los destinos permitidos (mismo criterio que el menú lateral)
  const primaryTabs = PRIMARY_TABS.filter((tab) => canGo(tab.to));
  const moreTabs = MORE_TABS.filter((tab) => canGo(tab.to));

  // Only render on mobile viewports
  if (!isMobile) return null;

  // Check if current path is in the "More" section (to highlight the More button)
  const isMoreActive = moreTabs.some((tab) => location.pathname.startsWith(tab.to));

  return (
    <>
      {moreOpen && moreTabs.length > 0 && <MoreSheet items={moreTabs} onClose={() => setMoreOpen(false)} />}

      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[var(--color-surface)]/95 backdrop-blur-md border-t border-[var(--color-border)] pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-14">
          {primaryTabs.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `
                  flex flex-col items-center justify-center gap-0.5 flex-1 h-full
                  transition-colors
                  ${isActive
                    ? 'text-[var(--color-primary)]'
                    : 'text-[var(--color-text-muted)]'
                  }
                `}
              >
                <Icon size={20} />
                <span className="text-[10px] font-medium">{t(item.label)}</span>
              </NavLink>
            );
          })}

          {/* More button */}
          {moreTabs.length > 0 && (
          <button
            onClick={() => setMoreOpen(true)}
            className={`
              flex flex-col items-center justify-center gap-0.5 flex-1 h-full
              transition-colors
              ${isMoreActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'}
            `}
          >
            <MoreHorizontal size={20} />
            <span className="text-[10px] font-medium">{t('nav.more', 'Más')}</span>
          </button>
          )}
        </div>
      </nav>
    </>
  );
}
